import { useState } from 'react';
import { Check, ChevronsUpDown, Building2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useOrganizations } from '@/hooks/useOrganizations';
import { CreateOrganizationDialog } from './CreateOrganizationDialog';

export function OrganizationSwitcher() {
  const [open, setOpen] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { organizations, currentOrganization, switchOrganization, loading } = useOrganizations();

  const handleOrganizationSelect = (organizationId: string) => {
    if (organizationId !== currentOrganization?.id) {
      switchOrganization(organizationId);
    }
    setOpen(false);
  };

  if (loading) {
    return (
      <Button variant="outline" className="justify-between w-[200px]" disabled>
        <Building2 className="mr-2 h-4 w-4" />
        Chargement...
      </Button>
    );
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between w-[200px]"
          >
            <div className="flex items-center">
              <Building2 className="mr-2 h-4 w-4" />
              {currentOrganization?.name || 'Personnel'}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0">
          <Command>
            <CommandInput placeholder="Rechercher une organisation..." />
            <CommandList>
              <CommandEmpty>Aucune organisation trouvée.</CommandEmpty>
              
              <CommandGroup heading="Personnel">
                <CommandItem
                  onSelect={() => handleOrganizationSelect('')}
                  className="cursor-pointer"
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  <span>Compte personnel</span>
                  {!currentOrganization && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </CommandItem>
              </CommandGroup>

              {organizations.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading="Organisations">
                    {organizations.map((org) => (
                      <CommandItem
                        key={org.id}
                        onSelect={() => handleOrganizationSelect(org.id)}
                        className="cursor-pointer"
                      >
                        <Building2 className="mr-2 h-4 w-4" />
                        <span>{org.name}</span>
                        {currentOrganization?.id === org.id && (
                          <Check className="ml-auto h-4 w-4" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    setShowCreateDialog(true);
                  }}
                  className="cursor-pointer"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <span>Créer une organisation</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <CreateOrganizationDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </>
  );
}